import { useState } from 'react';

import { useFlowNavigation } from '../hooks/useFlowNavigation';
import { useAppStore, useSetShowSettingsPopup, useShowSettingsPopup } from '../store/hooks';
import { FlowStep } from '../store/slices/flowSlice';

import LandingIntroCard from './LandingIntroCard';
import { ProjectManagerCard } from './ProjectManagerCard';
import { SettingsButton } from './SettingsButton';
import { SettingsPopup } from './SettingsPopup';
import { UnifiedBottomBar } from './UnifiedBottomBar';
import { UnifiedTopBar } from './UnifiedTopBar';

export default function LandingSplash() {
  const { goToNextStep } = useFlowNavigation();
  const showSettingsPopup = useShowSettingsPopup();
  const setShowSettingsPopup = useSetShowSettingsPopup();
  const createProject = useAppStore((s) => s.createProject);
  const loadProject = useAppStore((s) => s.loadProject);
  const projects = useAppStore((s) => s.projects);

  const [showNameDialog, setShowNameDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  // Check if projects exist and get the most recent one
  const projectList = Object.values(projects);
  const hasProjects = projectList.length > 0;
  const mostRecentProject = hasProjects
    ? projectList.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())[0]
    : null;

  const handleStartNewFlow = () => {
    const defaultName = `New Flow (${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`;
    setNewProjectName(defaultName);
    setShowNameDialog(true);
  };

  const handleContinueLastFlow = () => {
    if (mostRecentProject) {
      loadProject(mostRecentProject.id);
      const updated = useAppStore.getState().projects[mostRecentProject.id];
      if (updated && updated.flowState.currentStep === FlowStep.LANDING) {
        // Newly created projects will still be on the landing step
        // so advance to the first real step automatically
        goToNextStep();
      }
    }
  };

  const handleCreateAndStart = () => {
    const name = newProjectName.trim() || 'Untitled Project';
    const id = createProject(name);
    loadProject(id);
    setShowNameDialog(false);
    goToNextStep();
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#050614] to-[#0f1a2f] flex flex-col">
      {/* Header */}
      <UnifiedTopBar subtitle="Scene Planner" actions={<SettingsButton />} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center md:flex-row md:justify-between gap-6 lg:gap-8 px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 max-w-7xl mx-auto w-full">
        {/* Left Side - Project Manager */}
        <div className="w-full max-w-2xl sm:max-w-none md:w-60 lg:w-72 xl:w-96 md:flex-shrink-0 order-2 md:order-1">
          <ProjectManagerCard
            showCreateInput={false}
            onEmptyStateAction={handleStartNewFlow}
            className="h-full"
            maxProjects={4}
            showViewAllLink
          />
        </div>

        {/* Right Side - Intro Content */}
        <div className="flex-1 flex items-center justify-center order-1 md:order-2 min-h-[50vh] sm:min-h-[60vh] xl:min-h-0 md:max-w-2xl">
          <div className="w-full text-center px-1 sm:px-2">
            <div className="mb-8 sm:mb-10">
              <LandingIntroCard />
            </div>
          </div>
        </div>
      </div>

      {/* Name Dialog Modal */}
      {showNameDialog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#0c1222]/95 backdrop-blur border border-[#1a2d4d] rounded-xl p-8 max-w-lg w-full shadow-2xl animate-in fade-in duration-200">
            <div className="flex items-center mb-6">
              <div className="w-1 h-8 bg-gradient-to-b from-[#4f46e5] to-[#06b6d4] rounded-full mr-4"></div>
              <h3 className="text-2xl font-semibold text-white">Name Your New Flow</h3>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              Give your interactive story a memorable name. You can always change this later.
            </p>
            <input
              type="text"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateAndStart()}
              className="w-full bg-[#050614] border border-[#1a2d4d] rounded-lg p-4 text-white mb-6 focus:border-[#4f46e5] focus:outline-none transition-colors text-lg"
              placeholder="Enter project name"
              autoFocus
            />
            <div className="flex gap-4 justify-end">
              <button
                onClick={() => setShowNameDialog(false)}
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateAndStart}
                className="px-6 py-3 bg-gradient-to-r from-[#4f46e5] to-[#06b6d4] hover:from-[#4338ca] hover:to-[#0891b2] text-white rounded-lg transition-all duration-200 hover:scale-105 font-medium"
              >
                Create & Start
              </button>
            </div>
          </div>
        </div>
      )}

      <UnifiedBottomBar
        variant="planner"
        onBack={() => {}}
        onContinue={hasProjects ? handleContinueLastFlow : handleStartNewFlow}
        canContinue={true}
        continueLabel={hasProjects ? 'Continue Last Flow' : 'Start New Flow'}
        showBack={false}
        showUndo={false}
        showRedo={false}
        showClear={false}
        showActionsSeparator={false}
        rightContent={
          hasProjects ? (
            <>
              <button
                onClick={handleStartNewFlow}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                + Start New
              </button>
              <div className="w-px h-6 bg-[#1a2d4d] mx-2" />
            </>
          ) : undefined
        }
      />
      <SettingsPopup isOpen={showSettingsPopup} onClose={() => setShowSettingsPopup(false)} />
    </div>
  );
}
